import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { selectSchool } from '../actions';
import store from '../store';

import { format } from '../modules/helpers';
import filter from '../modules/filter-data';
import Tooltip from './tooltip';
import * as d3 from 'd3';

let svg;
let tooltip;

function makeEnrolment(data, cluster, chartName) {
  // Quick helper to update the totals when changing test data
  // SVG dimensions
  const width = 1000;
  const height = 500;
  const minHeight = 256;
  const padding = {
    top: 10,
    right: 10,
    bottom: 50,
    left: 10,
  };
  const numCols = 25;
  const numRows = 8;
  const xGutter = 0;
  const yGutter = 10;
  let activeSchool;
  let scaleFactor;
  let totalIcons = 0;
  // SVG padding
  // Horizontal and vertical spacing between the icons
  let offset = 0;

  // Top level elements
  let elm;

  let aside;
  let main;
  let key;
  let pictoGrid;

  // Final gid dimensions
  const xSize =
    (width - (padding.left + padding.right) - xGutter * (numCols - 1)) /
    numCols;
  const ySize =
    (height - (padding.top + padding.bottom) - yGutter * (numRows - 1)) /
    numRows;

  const student_data = filter.studentEnrolment(data.schools);

  function initialiseElements() {
    elm = document.createElement('div');

    aside = d3.select(elm).append('div').attr('class', 'aside');

    main = d3
      .select(elm)
      .append('div')
      .attr('class', 'main')
      .attr('aria-hidden', 'true');

    svg = main.append('svg').attr('viewBox', `0 0 ${width} ${height}`);
  }

  function setupToolTip() {
    const content = `<div class="tooltip-content-wrapper">
      <div class="tooltip-content">
      <div>
        <p class="tooltip-title"></p>
        <p class="tooltip-number"></p>
        <p class="tooltip-percent"></p>
        <p class="a11y">Press enter to navigate to this school's page or tab to select another school.</p>
      </div>
      </div>
    </div>`;

    tooltip = new Tooltip(content, 'tooltip-arrow');
    main.node().appendChild(tooltip.elm);
  }

  const handleClick = function(d) {
    if (d3.event) d3.event.stopPropagation();
    store.dispatch(
      selectSchool({
        [chartName]: {
          selectedSchool: d,
          showTooltip: true,
        },
      })
    );
  };

  function calculateTotalEnrolment() {
    student_data.forEach(obj => {
      if (obj.isActive) {
        activeSchool = obj;
      }
    });
  }

  function calculateScaleFactor() {
    scaleFactor = Math.ceil(data.enrolment / (numCols * numRows));

    // Determine if edgecase where rounding per group causes number of icons to be
    // greater than grid space available
    student_data.forEach(obj => {
      totalIcons += Math.max(Math.round(obj.enrolment / scaleFactor), 1);
    });

    // If edgecase adjust scale factor
    if (totalIcons > numCols * numRows) {
      scaleFactor = Math.ceil(totalIcons * scaleFactor / (numCols * numRows));
    }
  }

  function drawAside() {
    let card;

    if (activeSchool) {
      card = aside.append('div').attr('class', 'card--highlight');

      card.append('p').attr('class', 'card-title').text(activeSchool.name);

      card
        .append('p')
        .attr('class', 'big-number')
        .text(format(activeSchool.enrolment));
    }

    card = aside.append('div').attr('class', 'card--highlight-alt');

    card.append('p').attr('class', 'card-title').text(() => {
      if (activeSchool) {
        return `${cluster} schools group*`;
      }
      return `${cluster} schools group`;
    });

    card.append('p').attr('class', 'big-number').text(format(data.enrolment));

    if (activeSchool) {
      aside.append('p').text(`*inc. ${activeSchool.name}`);
    }
  }

  function drawPictograph() {
    svg.attr('class', 'cluster-chart');

    // define an icon store it in svg <defs> elements as a reusable component - this geometry can be generated from Inkscape, Illustrator or similar
    const defs = svg.append('defs');

    defs
      .append('symbol')
      .attr('viewBox', '0 0 24 37')
      .attr('id', 'student-icon')
      .append('path')
      .attr('stroke', 'none')
      .attr(
        'd',
        'M9.14285714,32 L9.14285714,23.3846154 L7,23.3846154 L7,15.4871795 C7,13.9076923 8.28571429,12.6153846 9.85714286,12.6153846 L14.1428571,12.6153846 C15.7142857,12.6153846 17,13.9076923 17,15.4871795 L17,23.3846154 L14.8571429,23.3846154 L14.8571429,32 L9.14285714,32 Z M12,11.1794872 C13.9821429,11.1794872 15.5714286,9.58205128 15.5714286,7.58974359 C15.5714286,5.5974359 13.9821429,4 12,4 C10.0178571,4 8.42857143,5.5974359 8.42857143,7.58974359 C8.42857143,9.58205128 10.0178571,11.1794872 12,11.1794872 Z'
      );

    const studentInAreaDef = defs
      .append('symbol')
      .attr('viewBox', '0 0 24 37')
      .attr('id', 'student-in-area-icon');

    studentInAreaDef
      .append('path')
      .attr('stroke', 'none')
      .attr(
        'd',
        'M9.14285714,32 L9.14285714,23.3846154 L7,23.3846154 L7,15.4871795 C7,13.9076923 8.28571429,12.6153846 9.85714286,12.6153846 L14.1428571,12.6153846 C15.7142857,12.6153846 17,13.9076923 17,15.4871795 L17,23.3846154 L14.8571429,23.3846154 L14.8571429,32 L9.14285714,32 Z M12,11.1794872 C13.9821429,11.1794872 15.5714286,9.58205128 15.5714286,7.58974359 C15.5714286,5.5974359 13.9821429,4 12,4 C10.0178571,4 8.42857143,5.5974359 8.42857143,7.58974359 C8.42857143,9.58205128 10.0178571,11.1794872 12,11.1794872 Z'
      );
    studentInAreaDef
      .append('ellipse')
      .attr('cx', 12)
      .attr('cy', 31.5)
      .attr('rx', 7)
      .attr('ry', 4)
      .attr('fill', 'none')
      .attr('stroke-width', 2)
      .attr('stroke', 'inherit');

    // create group element and create an svg <use> element for each icon
    pictoGrid = svg
      .append('g')
      .attr('id', 'pictoGrid')
      .attr('transform', `translate(${padding.left} ${padding.top})`);

    // ligo (LIve and GO to) refers to the percentage of the school's students
    // who actually live in that school's catchment. (Decimal, 0.0 -> 1.0)
    const addSchools = (group, range, iconName, ligoPercent) => {
      const schoolGroup = group.selectAll(`.${iconName}`);

      schoolGroup
        .data(range)
        .enter()
        .append('use')
        .attr('class', iconName)
        .attr('xlink:href', (d, i) => {
          const totalIcons = range.length;
          const ligoIcons = Math.round(totalIcons * ligoPercent);
          if (i + 1 <= ligoIcons) {
            // Students living inside the catchment boundary
            return '#student-in-area-icon';
          }
          // Students living outside the catchment boundary
          return '#student-icon';
        })
        .attr('width', xSize)
        .attr('height', ySize)
        .attr('id', d => `icon-${d}`)
        .attr('x', d => {
          const col = d % numCols; // calculates the x position (column number) using modulus
          return col * xGutter + col * xSize; // apply the buffer and return value
        })
        .attr('y', d => {
          const whole = Math.floor(d / numCols); // calculates the y position (row number)
          return whole * yGutter + whole * ySize; // apply the buffer and return the value
        });

      // This is a bit of a hack to make the whole group focusable
      schoolGroup
        .data(range)
        .enter()
        .append('rect')
        .attr('width', xSize + xGutter)
        .attr('height', ySize + yGutter)
        .attr('fill', 'transparent')
        .attr('id', d => `icon-${d}`)
        .attr('x', d => {
          const col = d % numCols; // calculates the x position (column number) using modulus
          return col * xGutter + col * xSize; // apply the buffer and return value
        })
        .attr('y', d => {
          const whole = Math.floor(d / numCols); // calculates the y position (row number)
          return whole * yGutter + whole * ySize; // apply the buffer and return the value
        });

      return schoolGroup;
    };

    const addSchoolGroup = data => {
      const groupStart = offset;
      const groupEnd =
        offset + Math.max(Math.round(data.enrolment / scaleFactor), 1);
      const students = d3.range(groupStart, groupEnd);

      offset = groupEnd;

      const schoolGroup = pictoGrid
        .append('g')
        .attr('tabindex', 0)
        .attr('id', `students-${data.uid}`)
        .attr('class', () => {
          const classnames = [];
          classnames.push('school-group');
          if (data.isActive) classnames.push('active-group');
          return classnames.join(' ');
        })
        .on('click', function() {
          handleClick.call(this, data);
        })
        .on('keydown', function() {
          const e = d3.event;
          const keys = {
            SPACE: 32,
            ENTER: 13,
          };
          if (
            e.type === 'click' ||
            e.keyCode === keys.SPACE ||
            e.keyCode === keys.ENTER
          ) {
            handleClick.call(this, data);
          }
        });

      addSchools(schoolGroup, students, 'student-icon', data.ligo);

      // No line at start!
      if (groupStart !== 0) {
        let col = groupStart % numCols;
        let row = Math.floor(groupStart / numCols);
        // If the new group starts on a new line move the
        // line to the last col of the previous row
        if (col === 0) {
          col = numCols;
          row = row - 1;
        }

        const xPos = col * xGutter + col * xSize;
        const yPos = row * yGutter + row * ySize;

        schoolGroup
          .append('line')
          .attr('x1', xPos - 1)
          .attr('y1', yPos + 4)
          .attr('x2', xPos - 1)
          .attr('y2', yPos + ySize)
          .attr('stroke-width', 2)
          .attr('stroke', '#333');
      }
    };

    // Main data loop
    student_data.forEach((obj, i) => {
      addSchoolGroup(obj);
    });
  }

  function drawKey() {
    key = main.append('div').attr('class', 'pictograph-key');
    const container = key.append('div');

    const key1 = container.append('div');

    key1
      .append('svg')
      .attr('class', 'student-icon in-area')
      .append('use')
      .attr('xlink:href', '#student-in-area-icon')
      .attr('stroke', '#000');

    key1
      .append('div')
      .text(`${scaleFactor} students living in their school area`);

    const key2 = container.append('div');

    key2
      .append('svg')
      .attr('class', 'student-icon')
      .append('use')
      .attr('xlink:href', '#student-icon');

    key2
      .append('div')
      .text(`${scaleFactor} students living outside their school area`);
  }

  function resizeViewbox() {
    const totalRows = Math.ceil(totalIcons / numCols);
    const viewboxHeight = Math.max(totalRows * (ySize + yGutter), minHeight);
    svg.attr('viewBox', `0 0 ${width} ${viewboxHeight}`);
  }

  initialiseElements();
  setupToolTip();
  calculateScaleFactor();
  resizeViewbox();
  drawKey();
  key
    .append('div')
    .style('padding', '8px 0 0 0')
    .style('width', '100%')
    .text('Data subject to change.*');
  calculateTotalEnrolment();
  drawAside();
  drawPictograph();

  elm.classList.add('chart');
  return elm;
}

class StudentEnrolment extends Component {
  wrapChart() {
    this.container = findDOMNode(this);
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    const { data } = this.props;
    const chart = makeEnrolment(data, data.name, this.props.chartName);
    this.container.appendChild(chart);
  }

  componentDidMount() {
    this.wrapChart();
  }

  componentDidUpdate() {
    if (d3.event) d3.event.stopPropagation();

    const { selectedSchool, showTooltip } = this.props.ui;
    if (showTooltip && selectedSchool) {
      svg.selectAll('.selected-group').classed('selected-group', false);
      svg
        .select(`#students-${selectedSchool.uid}`)
        .classed('selected-group', true);

      tooltip.onClick(function() {
        if (window.parentIFrame) {
          window.parentIFrame.sendMessage({
            type: 'change:school',
            school: selectedSchool.uid,
          });
        } else {
          window.location.search = `school=${selectedSchool.uid}`;
        }
      });

      tooltip.elm.classList.add('tooltip-arrow');
      tooltip.elm.classList.remove('tooltip-active');

      if (selectedSchool.isActive) {
        tooltip.elm.classList.remove('tooltip-arrow');
        tooltip.elm.classList.add('tooltip-active');
      }

      tooltip.show();

      // Custom Select
      tooltip.elm.querySelector('.tooltip-title').innerHTML =
        selectedSchool.name;

      tooltip.elm.querySelector(
        '.tooltip-number'
      ).innerHTML = `${selectedSchool.enrolment} enrolled students`;

      const percentText = isNaN(parseInt(selectedSchool.ligo * 100, 10))
        ? 'live in school area: unknown'
        : `${parseInt(selectedSchool.ligo * 100, 10)}% live in school area`;
      tooltip.elm.querySelector('.tooltip-percent').innerHTML = percentText;

      // Custom BB
      const bb = svg
        .select(`#students-${selectedSchool.uid}`)
        .node()
        .getBoundingClientRect();

      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const top = bb.top + scrollY - tooltip.elm.clientHeight - 10;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const left =
        bb.left + bb.width / 2 + scrollX - tooltip.elm.clientWidth / 2;
      tooltip.move(top, left);
    }
  }

  render() {
    return <div className="chart-wrapper" />;
  }
}

const mapStateToProps = (state, ownProps) => ({
  ui: state.ui[ownProps.chartName],
});

const mapDispatchToProps = dispatch => ({
  selectSchool: p => dispatch(selectSchool(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentEnrolment);



// WEBPACK FOOTER //
// ./src/components/students.js